import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	concatMap,
	endWith,
	exhaustMap,
	filter,
	from,
	map,
	of,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsScopeService } from '../../ajs-scope.service';
import { AjsStateService } from '../../ajs-state.service';
import { states } from '../../core/application.states';
import { DataFrameRequester } from '../../requesters/core/data-frame.requester';
import { DatablockRequester } from '../../requesters/datablock/datablock.requester';
import { DatablockItemEntity } from '../components/datablock-list/datablock-item.entity';
import { DatablockListEntity } from '../components/datablock-list/datablock-list.entity';
import { DatablockAdapter } from '../datablock.adapter';
import {
	bulkDelete,
	bulkDeleteFail,
	bulkDeleteSuccess,
	constructDatablockList,
	constructDatablockListFail,
	constructDatablockListSuccess,
	deleteDatablock,
	deleteDatablockFail,
	deleteDatablockSuccess,
	fetchDatablockListFail,
	fetchDatablockListSuccess,
	navigateToCreateFromDatablock,
	navigateToDatablock,
	navigateToDatablockCreate,
	navigateToDatablockDuplicate,
	navigateToExportDatablock,
	persistDatablocks,
	persistDatablockFail,
	persistDatablockStarted,
	persistDatablockStopped,
	persistenceActionFinish,
	persistenceActionsAccepted,
	refreshDatablockList,
	repersistDatablocks,
	repersistDatablockFail,
	repersistDatablockStarted,
	repersistDatablockStopped,
	stopPersistence,
	stopPersistenceFail,
	toggleDatablockStatus,
	trackAction,
	unpersistDatablocks,
	unpersistDatablockFail,
	unpersistDatablockStarted,
	unpersistDatablockStopped,
	updateDatablocksExpositionsCount,
	updateDatablocksExpositionsCountFailure,
	updateDatablocksExpositionsCountSuccess,
} from './datablock.actions';
import { DatablockSelector } from './datablock.selector';

interface IDatablockJobSocketResponse {
	element_id: number;
	type: string;
	message: string;
	data:
		| {
				job_status: string;
				client_id: string;
				job_id: string;
				persisted: boolean;
				persistence_date: number;
				persistence_execution_time: number;
				persistence_size: number;
				current_repartition_number: number;
		  }
		| undefined;
}
@Injectable()
export class DatablockEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################

	public navigateToDatablockCreate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToDatablockCreate),
				tap(() => this.stateService.go(states.DatablockCreate))
			),
		{
			dispatch: false,
		}
	);

	public navigateToDatablock = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToDatablock),
				tap((a) =>
					this.stateService.go(states.DatablockEdit, {
						id: `${a.id}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToDatablockDuplicate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToDatablockDuplicate),
				tap((a) =>
					this.stateService.go(states.DatablockDuplicate, {
						duplicatedId: `${a.id}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToCreateFromDatablock = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToCreateFromDatablock),
				tap((a) =>
					this.stateService.go(states.DatablockFromDatablock, {
						datablockId: `${a.id}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToExportDatablock = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToExportDatablock),
				tap((a) =>
					this.stateService.go(states.ExportDatablockFromList, {
						datablockId: `${a.id}`,
						datablockLabel: a.label,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	public constructDatablockList$ = createEffect(() =>
		this.action$.pipe(
			ofType(constructDatablockList),
			map(() =>
				constructDatablockListSuccess({
					datablockList: this.datablockAdapter.generateDatablockListStructure(),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					constructDatablockListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public initDatablockList$ = createEffect(() =>
		this.action$.pipe(
			ofType(constructDatablockListSuccess),
			switchMap(() => this.datablockRequester.getDatablockList(true)),
			map((response) =>
				fetchDatablockListSuccess({
					datablockList: this.datablockAdapter.generateDatablockList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchDatablockListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshDatablockList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshDatablockList),
			switchMap(() => this.datablockRequester.getDatablockList(false)),
			map((response) =>
				fetchDatablockListSuccess({
					datablockList: this.datablockAdapter.generateDatablockList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchDatablockListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deleteDatablock$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteDatablock),
			switchMap((action) =>
				this.datablockRequester.deleteDatablock(action.datablockId)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@datablock.delete.success:`
				);
				return deleteDatablockSuccess();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@datablock.delete.error:`
				);
				this.store.dispatch(deleteDatablockFail());
				return caught;
			})
		)
	);

	public bulkDeleteDatablocks$ = createEffect(() =>
		this.action$.pipe(
			ofType(bulkDelete),
			switchMap((action) =>
				this.datablockRequester.deleteMany(action.datablockId)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@datablock.delete.multiple.success:`
				);
				return bulkDeleteSuccess();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@datablock.delete.multiple.error:`
				);
				this.store.dispatch(bulkDeleteFail());
				return caught;
			})
		)
	);

	public persistDatablocks$ = createEffect(() =>
		this.action$.pipe(
			ofType(persistDatablocks),
			withLatestFrom(this.datablockSelector.getDatablockList$()),
			exhaustMap(([action, list]) =>
				from(
					this.resolveDatablocks(action.datablockIds, list).filter(
						(datablock) => !datablock.persistenceState.isPersisted
					)
				).pipe(
					concatMap((datablock) =>
						this.dataFrameRequester.persist(datablock.code).pipe(
							map((payload) =>
								trackAction({
									datablockId: datablock.id,
									jobId: payload.job_id,
								})
							),
							catchError((error: HttpErrorResponse) => {
								console.error(error);
								this.ngCommService.notifyOnError(
									$localize`:i18n=@@datablock.action.persist.error.message:
																			${datablock.label}:DATABLOCK_NAME:`
								);
								return of(
									persistDatablockFail({
										error,
									})
								);
							})
						)
					),
					endWith(persistenceActionsAccepted())
				)
			)
		)
	);

	public repersistDatablocks$ = createEffect(() =>
		this.action$.pipe(
			ofType(repersistDatablocks),
			withLatestFrom(this.datablockSelector.getDatablockList$()),
			exhaustMap(([action, list]) =>
				from(this.resolveDatablocks(action.datablockIds, list)).pipe(
					concatMap((datablock) =>
						this.dataFrameRequester.repersist(datablock.code).pipe(
							map((payload) =>
								trackAction({
									datablockId: datablock.id,
									jobId: payload.job_id,
								})
							),
							catchError((error: HttpErrorResponse) => {
								console.error(error);
								this.ngCommService.notifyOnError(
									$localize`:i18n=@@datablock.action.repersist.error.message:
																			${datablock.label}:DATABLOCK_NAME:`
								);
								return of(
									repersistDatablockFail({
										error,
									})
								);
							})
						)
					),
					endWith(persistenceActionsAccepted())
				)
			)
		)
	);

	public unpersistDatablocks$ = createEffect(() =>
		this.action$.pipe(
			ofType(unpersistDatablocks),
			withLatestFrom(this.datablockSelector.getDatablockList$()),
			exhaustMap(([action, list]) =>
				from(
					this.resolveDatablocks(action.datablockIds, list).filter(
						(datablock) => datablock.persistenceState.isPersisted
					)
				).pipe(
					concatMap((datablock) =>
						this.dataFrameRequester.unpersist(datablock.code).pipe(
							map((payload) =>
								trackAction({
									datablockId: datablock.id,
									jobId: payload.job_id,
								})
							),
							catchError((error: HttpErrorResponse) => {
								console.error(error);
								this.ngCommService.notifyOnError(
									$localize`:i18n=@@datablock.action.unpersist.error.message:
																			${datablock.label}:DATABLOCK_NAME:`
								);
								return of(
									unpersistDatablockFail({
										error,
									})
								);
							})
						)
					),
					endWith(persistenceActionsAccepted())
				)
			)
		)
	);

	public listenToWS$ = createEffect(() =>
		this.action$.pipe(
			ofType(constructDatablockListSuccess),
			switchMap(() =>
				from(
					this.ajsScopeService.listenOnRootScope<IDatablockJobSocketResponse>(
						'job_global'
					)
				)
			),
			filter(
				(stompResponse) =>
					stompResponse?.type === 'PersistFrame' ||
					stompResponse?.type === 'UnPersistFrame' ||
					stompResponse?.type === 'RepersistFrame'
			),
			withLatestFrom(this.datablockSelector.getDatablockList$()),
			tap(([stompResponse, datablockList]) => {
				if (stompResponse?.data?.job_status.toLocaleLowerCase() === 'running') {
					if (stompResponse?.type === 'PersistFrame') {
						this.store.dispatch(
							persistDatablockStarted({
								datablockId: stompResponse.element_id,
								jobId: stompResponse.data.job_id,
							})
						);
					}
					if (stompResponse?.type === 'UnPersistFrame') {
						this.store.dispatch(
							unpersistDatablockStarted({
								datablockId: stompResponse.element_id,
								jobId: stompResponse.data.job_id,
							})
						);
					}
					if (stompResponse?.type === 'RepersistFrame') {
						this.store.dispatch(
							repersistDatablockStarted({
								datablockId: stompResponse.element_id,
								jobId: stompResponse.data.job_id,
							})
						);
					}
				} else if (
					stompResponse?.data?.job_status.toLocaleLowerCase() === 'error'
				) {
					const { elements } = datablockList;
					const datablock = elements
						.toList()
						.toArray()
						.find((e) => e.code === stompResponse?.message);
					if (!datablock) {
						return;
					}

					let errorMessage = '';
					if (stompResponse?.type === 'PersistFrame') {
						errorMessage = $localize`:i18n=@@datablock.action.persist.error.message:
																			${datablock.label}:DATABLOCK_NAME:`;
						this.store.dispatch(
							persistDatablockStopped({
								datablockId: datablock.id,
								jobId: stompResponse.data.job_id,
							})
						);
					}

					if (stompResponse?.type === 'UnPersistFrame') {
						errorMessage = $localize`:i18n=@@datablock.action.unpersist.error.message:
																			${datablock.label}:DATABLOCK_NAME:`;
						this.store.dispatch(
							unpersistDatablockStopped({
								datablockId: datablock.id,
								jobId: stompResponse.data.job_id,
							})
						);
					}

					if (stompResponse?.type === 'RepersistFrame') {
						errorMessage = $localize`:i18n=@@datablock.action.repersist.error.message:
																			${datablock.label}:DATABLOCK_NAME:`;
						this.store.dispatch(
							repersistDatablockStopped({
								datablockId: datablock.id,
								jobId: stompResponse.data.job_id,
							})
						);
					}

					this.ngCommService.notifyOnError(errorMessage);
				}
			}),
			map(([stompResponse]) => stompResponse),
			filter(
				(stompResponse) =>
					stompResponse?.data?.job_status.toLocaleLowerCase() !== 'running' &&
					stompResponse?.data?.job_status.toLocaleLowerCase() !== 'waiting'
			),
			map((stompResponse) =>
				persistenceActionFinish({
					datablockId: stompResponse.element_id,
					persistenceInfo: {
						isPersisted: stompResponse.data?.persisted ?? false,
						persistenceDuration:
							stompResponse.data?.persistence_execution_time ?? 0,
						persistenceSize: stompResponse.data?.persistence_size ?? 0,
						persistenceDate: stompResponse.data?.persistence_date
							? new Date(stompResponse.data.persistence_date)
							: null,
						partitionCount: stompResponse.data?.current_repartition_number ?? 0,
					},
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshDatablockList());
				return caught;
			})
		)
	);

	public toggleActivationStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(toggleDatablockStatus),
			switchMap((action) =>
				this.datablockRequester.getDatablock(action.datablockId)
			),
			switchMap((datablock) =>
				this.datablockRequester.updateDatablock({
					...datablock,
					metadata: {
						...datablock.metadata,
						actif: !datablock.metadata?.actif,
					},
				})
			),
			map(() => refreshDatablockList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshDatablockList());
				return caught;
			})
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################

	public stopOngoingPersistence$ = createEffect(() =>
		this.action$.pipe(
			ofType(stopPersistence),
			switchMap((action) =>
				this.datablockRequester.stopPersistence(action.jobKey)
			),
			map(() => refreshDatablockList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					stopPersistenceFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getDatablocksExpositionsCount$ = createEffect(() =>
		this.action$.pipe(
			ofType(updateDatablocksExpositionsCount),
			switchMap((action) =>
				this.datablockRequester.getDatablocksExpositionsCount(
					action.ids,
					!action.shouldReload
				)
			),
			withLatestFrom(this.datablockSelector.getDatablockList$()),
			map(([response, currentList]) =>
				updateDatablocksExpositionsCountSuccess({
					datablockList: this.datablockAdapter.updateListWithExpositionsCount(
						currentList,
						response
					),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					updateDatablocksExpositionsCountFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public constructor(
		private readonly action$: Actions,
		private readonly datablockSelector: DatablockSelector,
		private readonly datablockRequester: DatablockRequester,
		private readonly datablockAdapter: DatablockAdapter,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly ngCommService: NgCommService,
		private readonly dataFrameRequester: DataFrameRequester,
		private readonly ajsScopeService: AjsScopeService
	) {}

	private resolveDatablocks(
		ids: Array<number>,
		list: DatablockListEntity
	): Array<DatablockItemEntity> {
		return ids
			.map((id) => list.elements.get(`${id}`))
			.filter(
				(datablock): datablock is DatablockItemEntity => datablock !== undefined
			);
	}
}
